import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap";
import {
  FaUpload,
  FaDownload,
  FaCheck,
  FaExclamationTriangle,
  FaTimes,
  FaUsers,
  FaUserCheck,
  FaUserTimes,
  FaUserFriends,
  FaFileExcel,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa";
import { BiGroup, BiSave } from "react-icons/bi";
import * as XLSX from "xlsx";
import addRecordWithId from "../../functions/addRecordWithId";
import { logBusinessAction } from "../../services/AuditService";
import firebaseApp from "../../firebase/credenciales";
import {
  collection,
  deleteDoc,
  getDoc,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
  getFirestore,
  query,
  where,
  updateDoc,
  orderBy
} from "firebase/firestore";
import Swal from "sweetalert2";
import { syncGroupState } from "../../services/groupAssignmentService";

const db = getFirestore(firebaseApp);

const GruposMasivos = ({ usuario }) => {
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [users, setUsers] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [warning, setWarning] = useState("");
  const rucEmpresa = usuario.ruc;

  const [gruposExistentes, setGruposExistentes] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true); // Estado global de carga inicial
  const [sedes, setSedes] = useState([]); // Estado para sedes disponibles

  // Estados para indicadores
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [usuariosEnGrupos, setUsuariosEnGrupos] = useState(0);
  const [usuariosSinGrupo, setUsuariosSinGrupo] = useState(0);
  const [totalGrupos, setTotalGrupos] = useState(0);

  // Cargar usuarios y grupos al inicializar o cuando el RUC cambie
  useEffect(() => {
    const fetchData = async () => {
      if (rucEmpresa) {
        setIsDataLoading(true);
        try {
          // Ejecutamos las llamadas principales
          // fetchUsers y los otros son async y devuelven promesas
          await Promise.all([
            fetchUsers(),
            fetchGruposExistentes(),
            fetchSedes(),
          ]);
        } catch (error) {
          console.error("Error cargando datos iniciales:", error);
        } finally {
          setIsDataLoading(false);
        }
      }
    };
    fetchData();
  }, [rucEmpresa]);

  // Calcular indicadores cuando cambien usuarios o grupos
  useEffect(() => {
    if (users.length > 0 && gruposExistentes.length >= 0) {
      // Calcular usuarios en grupos
      const allGroupMembers = new Set();
      gruposExistentes.forEach((group) => {
        if (group.members) {
          group.members.forEach((dni) => allGroupMembers.add(dni.toString()));
        }
      });

      const enGrupos = Array.from(allGroupMembers).length;
      const sinGrupo = users.length - enGrupos;

      setTotalUsuarios(users.length);
      setUsuariosEnGrupos(enGrupos);
      setUsuariosSinGrupo(sinGrupo);
      setTotalGrupos(gruposExistentes.length);
    }
  }, [users, gruposExistentes]);

  // Cargar usuarios de la base de datos
  const fetchUsers = async () => {
    try {
      const usersCollectionRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersCollectionRef);

      const usersData = [];
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.activo !== false && userData.rucEmpresa === rucEmpresa) {
          usersData.push({
            ...userData,
            id: doc.id,
          });
        }
      });

      setUsers(usersData);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
      setError("Error al cargar usuarios de la base de datos");
    }
  };

  const fetchGruposExistentes = async () => {
    setLoadingGrupos(true);
    try {
      const gruposRef = collection(db, `grupos/${rucEmpresa}/grupos`);
      const snapshot = await getDocs(gruposRef);
      const grupos = [];
      snapshot.forEach((doc) => {
        grupos.push({ id: doc.id, ...doc.data() });
      });
      setGruposExistentes(grupos);
    } catch (error) {
      setError("Error al cargar los grupos existentes");
    } finally {
      setLoadingGrupos(false);
    }
  };

  // Cargar sedes de la empresa (para vinculación automática)
  const fetchSedes = async () => {
    try {
      if (!rucEmpresa) return;
      const sedesCollectionRef = collection(db, `company_cordenadas/${rucEmpresa}/sedes`);
      const q = query(sedesCollectionRef, orderBy('nombre'));
      const sedesSnapshot = await getDocs(q);

      const sedesData = [];
      sedesSnapshot.forEach((doc) => {
        const data = doc.data();
        sedesData.push({
          id: doc.id,
          ...data
        });
      });
      setSedes(sedesData);
    } catch (error) {
      console.error("Error al obtener sedes:", error);
    }
  };

  // Manejar cambio de archivo
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (
      !selectedFile.name.endsWith(".xlsx") &&
      !selectedFile.name.endsWith(".xls")
    ) {
      setError("Solo se permiten archivos Excel (.xlsx, .xls)");
      return;
    }

    setFile(selectedFile);
    setError("");
    setSuccess("");
    setWarning("");
  };

  // Procesar archivo Excel
  const processExcel = async () => {
    if (!file) {
      setError("Por favor seleccione un archivo Excel");
      return;
    }

    setLoading(true);
    setError("");
    setWarning("");

    try {
      const data = await readExcelFile(file);
      setExcelData(data);
      validateData(data);
    } catch (error) {
      console.error("Error al procesar Excel:", error);
      setError("Error al procesar el archivo Excel");
    } finally {
      setLoading(false);
    }
  };

  // Leer archivo Excel
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // Validar datos del Excel
  const validateData = (data) => {
    const results = [];
    const processedGroups = [];

    // Detectar si las columnas están invertidas
    let isColumnsInverted = false;
    if (data.length > 0) {
      const firstRow = data[0];
      const groupValue = firstRow["Grupo"] || firstRow["grupo"] || "";
      const dniValue = firstRow["DNI"] || firstRow["dni"] || "";

      // Si "Grupo" parece un DNI (8 o 9 dígitos) y "DNI" parece un nombre de grupo (texto)
      if (
        /^\d{8,9}$/.test(groupValue.toString().trim()) &&
        typeof dniValue === "string" &&
        dniValue.trim().length > 0 &&
        !/^\d{8,9}$/.test(dniValue.toString().trim())
      ) {
        isColumnsInverted = true;
        console.log(
          "Columnas detectadas como invertidas. Corrigiendo automáticamente..."
        );
      }
    }

    data.forEach((row, index) => {
      let groupName = row["Grupo"] || row["grupo"] || "";
      let dni = row["DNI"] || row["dni"] || "";

      // Si las columnas están invertidas, intercambiar valores
      if (isColumnsInverted) {
        [groupName, dni] = [dni, groupName];
      }

      const result = {
        row: index + 2, // +2 porque Excel empieza en 1 y hay header
        groupName: groupName,
        dni: dni,
        errors: [],
        warnings: [],
        valid: true,
      };

      // Validar nombre del grupo
      if (!result.groupName || !result.groupName.toString().trim()) {
        result.errors.push("Nombre del grupo es requerido");
        result.valid = false;
      }

      // Validar DNI
      if (!result.dni) {
        result.errors.push("DNI es requerido");
        result.valid = false;
      } else {
        const dniStr = result.dni.toString().trim();
        if (!/^\d{8,11}$/.test(dniStr)) {
          result.errors.push("DNI inválido");
          result.valid = false;
        }

        // Verificar si el usuario existe (comparando como string)
        const user = users.find((u) => u.dni && u.dni.toString() === dniStr);
        if (!user) {
          result.errors.push("Usuario no encontrado en la base de datos");
          result.valid = false;
        } else {
          result.userName = `${user.name} ${user.apellidos}`;
          result.userEmail = user.email;
        }
      }

      results.push(result);
    });

    // ... (resto de la lógica de agrupación sin cambios significativos)
    const groupedData = {};
    results.forEach((result) => {
      if (result.valid) {
        if (!groupedData[result.groupName]) {
          groupedData[result.groupName] = [];
        }
        groupedData[result.groupName].push(result.dni.toString().trim());
      }
    });

    Object.keys(groupedData).forEach((groupName) => {
      const membersFromExcel = groupedData[groupName];
      const normalizedGroupName = groupName.trim().toLowerCase();

      let existingGroup = null;
      let isExactMatch = false;

      existingGroup = gruposExistentes.find(g => g.name === groupName);
      if (existingGroup) {
        isExactMatch = true;
      } else {
        existingGroup = gruposExistentes.find(g => g.name && g.name.trim().toLowerCase() === normalizedGroupName);
      }

      let groupInfo = {
        name: groupName,
        members: membersFromExcel,
        memberCount: membersFromExcel.length,
        exists: !!existingGroup,
        id: existingGroup ? existingGroup.id : null, 
        isExactMatch: isExactMatch,
        existingMembers: existingGroup ? existingGroup.members || [] : [],
        usuariosAAgregar: [],
        usuariosAEliminar: [],
        status: existingGroup ? "existente" : "nuevo",
        fullData: existingGroup
      };

      if (existingGroup) {
        const existingMembersStr = (existingGroup.members || []).map(dni => dni.toString());
        groupInfo.usuariosAAgregar = membersFromExcel.filter(dni => !existingMembersStr.includes(dni));
        groupInfo.usuariosAEliminar = [];
      } else {
        groupInfo.usuariosAAgregar = [...membersFromExcel];
      }

      processedGroups.push(groupInfo);
    });

    const existingGroupsCount = processedGroups.filter(g => g.exists).length;
    if (existingGroupsCount > 0) {
      setWarning(`${existingGroupsCount} grupo(s) existente(s) detectado(s). Se fusionarán los nuevos miembros con los existentes.`);
    } else {
      setWarning("");
    }

    setValidationResults(results);
    setProcessedData(processedGroups);
  };

  // Guardar grupos en Firebase (Modo FUSIONAR/MERGE)
  const saveGroups = async () => {
    if (processedData.length === 0) {
      setError("No hay grupos válidos para guardar");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const group of processedData) {
        try {
          const normalizedGroupName = group.name.trim().toLowerCase();
          const membersFromExcel = group.members.map((dni) => dni.toString());

          // Usamos los datos ya procesados en validateData para evitar consultas a Firestore en el bucle
          let existingGroupDoc = null;
          let existingGroupId = group.id;
          let existingGroupData = null;

          if (group.exists && group.fullData) {
            existingGroupDoc = { ref: doc(db, `grupos/${rucEmpresa}/grupos`, group.id) };
            existingGroupData = group.fullData;
          }

          if (existingGroupDoc && existingGroupData) {
            // FUSIONAR: Combinar miembros existentes con los nuevos
            const docRef = existingGroupDoc.ref;
            const existingMembers = existingGroupData.members || [];
            const existingMembersStr = existingMembers.map((dni) => dni.toString());

            const usuariosAAgregar = membersFromExcel.filter(
              (dni) => !existingMembersStr.includes(dni)
            );

            const finalMembersSet = new Set([...existingMembersStr, ...membersFromExcel]);
            const finalMembers = Array.from(finalMembersSet);

            if (usuariosAAgregar.length > 0) {
              const confirmText = `El grupo "${group.name}" ya tiene ${existingMembersStr.length} miembros.\nSe agregarán ${usuariosAAgregar.length} nuevos miembros.\nTotal final: ${finalMembers.length} miembros.`;

              // Ocultar spinner temporalmente y forzar el renderizado de React
              setProcessing(false);
              await new Promise(resolve => setTimeout(resolve, 100)); // Garantiza que el overlay se desmonte
              
              const result = await Swal.fire({
                title: `Actualizar grupo "${group.name}"`,
                text: confirmText,
                icon: "info",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Sí, agregar miembros",
                cancelButtonText: "Saltar este grupo",
              });
              
              setProcessing(true); // Re-activar spinner

              if (!result.isConfirmed) {
                console.log(`⏭️ Usuario canceló la actualización del grupo "${group.name}"`);
                continue;
              }

              await updateDoc(docRef, {
                members: finalMembers,
                description: `Grupo actualizado (Fusión) - ${new Date().toLocaleDateString()}`,
                updatedAt: serverTimestamp(),
              });

              const existingSedes = existingGroupData.sedes || [];
              let sedesIdsToAssign = [...existingSedes];

              if (sedesIdsToAssign.length === 0) {
                const matchingSede = sedes.find(s =>
                  s.nombre.toLowerCase() === normalizedGroupName ||
                  (s.regimenNombre && s.regimenNombre.toLowerCase() === normalizedGroupName)
                );
                if (matchingSede) {
                  sedesIdsToAssign = [matchingSede.id];
                  await updateDoc(docRef, { sedes: sedesIdsToAssign });
                }
              }

              await syncGroupState({
                rucEmpresa,
                groupId: existingGroupId,
                groupName: group.name,
                currentMemberDnis: finalMembers,
                currentSedeIds: sedesIdsToAssign,
                previousMemberDnis: existingGroupData.members || [],
                previousSedeIds: existingGroupData.sedes || [],
                sedesData: sedes,
                companyName: users[0]?.empresa || "Mi Empresa",
              });
            } else {
              console.log(`ℹ️ El grupo "${group.name}" ya contiene todos los miembros del Excel.`);
              await updateDoc(docRef, {
                updatedAt: serverTimestamp(),
              });
            }
          } else {
            // NUEVO GRUPO
            console.log(`🆕 Creando nuevo grupo "${group.name}"`);

            const groupData = {
              name: group.name,
              description: `Grupo creado masivamente - ${new Date().toLocaleDateString()}`,
              members: membersFromExcel,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              sedes: []
            };

            const matchingSede = sedes.find(s =>
              s.nombre.toLowerCase() === normalizedGroupName ||
              (s.regimenNombre && s.regimenNombre.toLowerCase() === normalizedGroupName)
            );

            if (matchingSede) {
              groupData.sedes = [matchingSede.id];
            }

            const groupsCollection = collection(db, `grupos/${rucEmpresa}/grupos`);
            const newGroupRef = doc(groupsCollection);
            await setDoc(newGroupRef, groupData);

            await syncGroupState({
              rucEmpresa,
              groupId: newGroupRef.id,
              groupName: group.name,
              currentMemberDnis: membersFromExcel,
              currentSedeIds: groupData.sedes || [],
              previousMemberDnis: [],
              previousSedeIds: [],
              sedesData: sedes,
              companyName: users[0]?.empresa || "Mi Empresa",
            });

            console.log(`✅ Nuevo grupo "${group.name}" creado con ID: ${newGroupRef.id}`);
          }

          successCount++;
        } catch (error) {
          console.error(`❌ Error al guardar grupo ${group.name}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        setSuccess(
          `${successCount} grupo(s) procesado(s) correctamente`
        );
        Swal.fire({
          icon: "success",
          title: "Proceso completado",
          text: `Se han procesado ${successCount} grupo(s) correctamente`,
          timer: 3000,
        });
        setFile(null);
        setExcelData([]);
        setProcessedData([]);
        setValidationResults([]);
        document.getElementById("fileInput").value = "";
        fetchGruposExistentes();

        // Registrar auditoría
        await logBusinessAction(
          "CARGA_MASIVA_GRUPOS",
          "GestionUsuarios",
          { cantidad: successCount },
          usuario
        );
      }

      if (errorCount > 0) {
        setError(`${errorCount} grupo(s) no pudieron ser procesados`);
      }
    } catch (error) {
      console.error("Error general al guardar grupos:", error);
      setError("Error al procesar los grupos");
    } finally {
      setProcessing(false);
    }
  };

  // Descargar plantilla Excel
  const downloadTemplate = () => {
    const templateData = [
      { Grupo: "Grupo Ejemplo 1", DNI: "12345678" },
      { Grupo: "Grupo Ejemplo 1", DNI: "87654321" },
      { Grupo: "Grupo Ejemplo 2", DNI: "11223344" },
      { Grupo: "Grupo Ejemplo 2", DNI: "44332211" },
      { Grupo: "Grupo Ejemplo 3", DNI: "123456789" }, // Ejemplo con 9 dígitos para extranjeros
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
    XLSX.writeFile(wb, "plantilla_grupos.xlsx");
  };

  const validCount = validationResults.filter((r) => r.valid).length;
  const invalidCount = validationResults.filter((r) => !r.valid).length;

  // Función para eliminar grupo con doble confirmación
  const handleDeleteGroup = async (group) => {
    // Primera confirmación (Warning)
    const result1 = await Swal.fire({
      title: '¿Eliminar grupo?',
      text: `Estás a punto de eliminar el grupo "${group.name}". Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar'
    });

    if (!result1.isConfirmed) return;

    // Segunda confirmación (Específica)
    const result2 = await Swal.fire({
      title: 'Confirmación final',
      text: `Por favor, escribe el nombre del grupo "${group.name}" para confirmar la eliminación:`,
      input: 'text',
      inputPlaceholder: group.name,
      showCancelButton: true,
      confirmButtonText: 'Eliminar definitivamente',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
      preConfirm: (inputValue) => {
        if (inputValue !== group.name) {
          Swal.showValidationMessage('El nombre ingresado no coincide');
        }
      }
    });

    if (!result2.isConfirmed) return;

    // Ejecutar eliminación
    try {
      setLoadingGrupos(true);
      const groupRef = doc(db, `grupos/${rucEmpresa}/grupos`, group.id);
      await deleteDoc(groupRef);

      await Swal.fire(
        '¡Eliminado!',
        `El grupo "${group.name}" ha sido eliminado correctamente.`,
        'success'
      );

      // Recargar lista
      fetchGruposExistentes();

    } catch (error) {
      console.error("Error al eliminar grupo:", error);
      Swal.fire(
        'Error',
        'Hubo un problema al eliminar el grupo.',
        'error'
      );
    } finally {
      setLoadingGrupos(false);
    }
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado azul para Grupos Masivos */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              borderBottom: "3px solid #f59e0b",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <BiGroup className="me-3" />
                Carga Masiva de Grupos
              </h2>
              <p className="mb-0 opacity-75">
                Importa y gestiona grupos de usuarios desde archivos Excel
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Indicador de carga para operaciones */}
            {(loading || processing) && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
                style={{ zIndex: 1040 }}
              >
                <div className="text-center">
                  <Spinner
                    animation="border"
                    variant="light"
                    style={{ width: "3rem", height: "3rem" }}
                  />
                  <p className="text-white mt-3 mb-0">
                    {loading ? "Procesando archivo..." : "Guardando grupos..."}
                  </p>
                  <p className="text-white-50 small">
                    Esto puede tomar unos minutos
                  </p>
                </div>
              </div>
            )}

            {/* Alertas */}
            {error && (
              <Alert
                variant="danger"
                onClose={() => setError("")}
                dismissible
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                variant="success"
                onClose={() => setSuccess("")}
                dismissible
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                {success}
              </Alert>
            )}
            {warning && (
              <Alert
                variant="warning"
                onClose={() => setWarning("")}
                dismissible
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                <FaExclamationTriangle className="me-2" />
                {warning}
              </Alert>
            )}

            {/* Barra superior con información */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                        color: "white",
                      }}
                    >
                      <BiGroup size={24} />
                    </div>
                  </Col>
                  <Col md={11}>
                    <h5 className="mb-1 fw-semibold">
                      Importación Masiva de Grupos
                    </h5>
                    <p className="text-muted mb-0 small">
                      Carga múltiples grupos de trabajo de forma rápida y eficiente desde archivos Excel
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas rápidas */}
            {!loading && (
              <Row className="mb-4">
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaUsers size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{totalUsuarios}</h4>
                      <p className="mb-0">Total de Usuarios</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaUserCheck size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{usuariosEnGrupos}</h4>
                      <p className="mb-0">En Grupos</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaUserTimes size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{usuariosSinGrupo}</h4>
                      <p className="mb-0">Sin Grupo</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaUserFriends size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{totalGrupos}</h4>
                      <p className="mb-0">Total de Grupos</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Sección de instrucciones */}
            <Card
              className="mb-4 border-0 shadow-sm"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaFileExcel className="me-2" />
                  <h6 className="mb-0 fw-semibold">Instrucciones de Uso</h6>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  <Col md={8}>
                    <h6 className="fw-semibold mb-3">Pasos a seguir:</h6>
                    <ol className="mb-3">
                      <li>
                        Descarga la plantilla Excel haciendo clic en el botón
                        "Descargar Plantilla"
                      </li>
                      <li>
                        Completa el archivo con los datos de los grupos:
                        <ul className="mt-2">
                          <li>
                            <strong>Grupo:</strong> Nombre del grupo
                          </li>
                          <li>
                            <strong>DNI:</strong> DNI del integrante (8 o 9 dígitos)
                          </li>
                        </ul>
                      </li>
                      <li>Cada fila representa un integrante del grupo</li>
                      <li>Puedes tener múltiples integrantes por grupo</li>
                      <li>
                        Sube el archivo completado y haz clic en "Procesar Excel"
                      </li>
                    </ol>
                  </Col>
                  <Col md={4} className="d-flex align-items-center">
                    <Button
                      variant="success"
                      onClick={downloadTemplate}
                      className="w-100 d-flex align-items-center justify-content-center"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                        border: "none",
                        boxShadow: "0 4px 8px rgba(16, 185, 129, 0.3)",
                      }}
                    >
                      <FaDownload className="me-2" />
                      Descargar Plantilla
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Row className="g-4">
              {/* Sección de carga de archivo */}
              <Col md={12}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <FaCloudUploadAlt className="me-2" />
                      <h6 className="mb-0 fw-semibold">Subir Archivo Excel</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="align-items-end">
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            Seleccionar archivo Excel
                          </Form.Label>
                          <Form.Control
                            id="fileInput"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            className="form-control-lg"
                            style={{
                              borderRadius: "10px",
                              border: "2px dashed #dee2e6",
                              padding: "20px",
                            }}
                          />
                          <Form.Text className="text-muted">
                            {file
                              ? `Archivo seleccionado: ${file.name}`
                              : "Selecciona el archivo Excel con los datos de los grupos"
                            }
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Button
                          variant="primary"
                          onClick={processExcel}
                          disabled={!file || loading || isDataLoading}
                          className="w-100 d-flex align-items-center justify-content-center py-3"
                          style={{
                            borderRadius: "10px",
                            background: file && !isDataLoading
                              ? "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)"
                              : "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)",
                            border: "none",
                            boxShadow: file && !isDataLoading
                              ? "0 4px 8px rgba(59, 130, 246, 0.3)"
                              : "none",
                          }}
                        >
                          {isDataLoading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Cargando sistema...
                            </>
                          ) : loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Procesando...
                            </>
                          ) : (
                            <>
                              <FaUpload className="me-2" />
                              Procesar Excel
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Resultados de validación */}
            {validationResults.length > 0 && (
              <Card
                className="mb-4 border-0 shadow-sm mt-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FaCheckCircle className="me-2" />
                      <h6 className="mb-0 fw-semibold">Resultados de Validación</h6>
                    </div>
                    <div className="d-flex gap-2">
                      <Badge bg="success" style={{ fontSize: "0.9rem" }}>
                        <FaCheck className="me-1" />
                        Válidos: {validCount}
                      </Badge>
                      <Badge bg="danger" style={{ fontSize: "0.9rem" }}>
                        <FaTimes className="me-1" />
                        Inválidos: {invalidCount}
                      </Badge>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table
                      responsive
                      hover
                      className="mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Fila
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Grupo
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            DNI
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Usuario
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Estado
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Errores
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResults.map((result, index) => (
                          <tr
                            key={index}
                            style={{ borderBottom: "1px solid #f1f3f4" }}
                            className={result.valid ? "" : "table-danger"}
                          >
                            <td className="text-center py-3 px-3">
                              <Badge
                                bg="dark"
                                className="px-2 py-1"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {result.row}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 fw-medium">
                              {result.groupName}
                            </td>
                            <td className="py-3 px-3">
                              <Badge
                                bg="secondary"
                                className="px-2 py-1"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {result.dni}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-muted small">
                              {result.userName || "-"}
                            </td>
                            <td className="text-center py-3 px-3">
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                {result.valid ? (
                                  <FaCheckCircle className="text-success" size="16px" />
                                ) : (
                                  <FaTimesCircle className="text-danger" size="16px" />
                                )}
                                <Badge bg={result.valid ? "success" : "danger"}>
                                  {result.valid ? "Válido" : "Inválido"}
                                </Badge>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              {result.errors.length > 0 && (
                                <ul className="mb-0 small text-danger">
                                  {result.errors.map((error, i) => (
                                    <li key={i}>{error}</li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Resumen de grupos procesados */}
            {processedData.length > 0 && (
              <Card
                className="mb-4 border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <BiGroup className="me-2" />
                      <h6 className="mb-0 fw-semibold">Grupos Procesados</h6>
                    </div>
                    <div className="d-flex gap-2">
                      <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                        Total: {processedData.length}
                      </Badge>
                      <Badge bg="success" style={{ fontSize: "0.8rem" }}>
                        Nuevos: {processedData.filter(g => !g.exists).length}
                      </Badge>
                      <Badge bg="warning" style={{ fontSize: "0.8rem" }}>
                        Existentes: {processedData.filter(g => g.exists).length}
                      </Badge>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <Table
                      responsive
                      hover
                      className="mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Nombre del Grupo
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Estado
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Integrantes
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Cambios
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            DNIs
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {processedData.map((group, index) => (
                          <tr key={index} style={{ borderBottom: "1px solid #f1f3f4" }}>
                            <td className="fw-bold py-3 px-3">{group.name}</td>
                            <td className="text-center py-3 px-3">
                              {group.exists ? (
                                <Badge bg="warning">Existente</Badge>
                              ) : (
                                <Badge bg="success">Nuevo</Badge>
                              )}
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge bg="primary">{group.memberCount}</Badge>
                            </td>
                            <td className="py-3 px-3">
                              {group.exists ? (
                                <div>
                                  <small className="text-success">
                                    +{group.usuariosAAgregar.length} agregar
                                  </small>
                                  <br />
                                  <small className="text-danger">
                                    -{group.usuariosAEliminar.length} eliminar
                                  </small>
                                </div>
                              ) : (
                                <small className="text-muted">Nuevo grupo</small>
                              )}
                            </td>
                            <td className="py-3 px-3">
                              <small>{group.members.join(", ")}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
                <Card.Footer
                  className="p-3"
                  style={{ backgroundColor: "#f8f9fa", borderRadius: "0 0 15px 15px" }}
                >
                  <Button
                    variant="success"
                    size="lg"
                    onClick={saveGroups}
                    disabled={processing}
                    className="w-100 d-flex align-items-center justify-content-center"
                    style={{
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                      border: "none",
                      boxShadow: "0 4px 8px rgba(16, 185, 129, 0.3)",
                    }}
                  >
                    {processing ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Guardando grupos...
                      </>
                    ) : (
                      <>
                        <BiSave className="me-2" />
                        Guardar {processedData.length} Grupo(s)
                      </>
                    )}
                  </Button>
                </Card.Footer>
              </Card>
            )}

            {/* Grupos Existentes */}
            <Card
              className="mb-4 border-0 shadow-sm"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaUserFriends className="me-2" />
                  <div>
                    <h6 className="mb-0 fw-semibold">Grupos Existentes</h6>
                    <small className="opacity-75">{gruposExistentes.length} grupos registrados</small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {loadingGrupos ? (
                  <div className="text-center p-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando grupos...
                  </div>
                ) : gruposExistentes.length === 0 ? (
                  <div className="text-center p-5">
                    <FaUserFriends size="3rem" className="text-muted mb-3" />
                    <p className="text-muted mb-0">No hay grupos registrados.</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <Table
                      responsive
                      hover
                      className="mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Nombre
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Descripción
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Integrantes
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Creado
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {gruposExistentes.map((grupo) => (
                          <tr key={grupo.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                            <td className="fw-semibold py-3 px-3">{grupo.name}</td>
                            <td className="py-3 px-3 text-muted small">
                              {grupo.description || "Sin descripción"}
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge bg="primary">
                                {Array.isArray(grupo.members)
                                  ? grupo.members.length
                                  : 0}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-muted small">
                              {grupo.createdAt?.toDate
                                ? grupo.createdAt.toDate().toLocaleDateString()
                                : "-"}
                            </td>
                            <td className="text-center py-3 px-3">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteGroup(grupo)}
                                style={{ borderRadius: "8px" }}
                                title="Eliminar grupo"
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default GruposMasivos;
